// Thin fetch wrapper shared by admin/ and portal/. Session-cookie auth
// (`sneethel.sid`, express-session) — always sends credentials so the
// cookie round-trips. JSON in, JSON out. Every non-2xx response is turned
// into an ApiError carrying the server's `{code}` (never the raw message
// rendered directly in a component — CLAUDE.md: never show a raw error
// string from the server; components map `error.code` to a localized
// string via shared/i18n instead).
//
// Base URL: empty string by default, i.e. same-origin relative paths. In
// dev this relies on Vite's dev-server proxy (see admin/vite.config.js) so
// the browser only ever talks to one origin and the session cookie's
// `sameSite: 'lax'` never has to survive a real cross-origin request — the
// backend (backend/src/app.js) has no CORS middleware at all, and this repo
// forbids editing backend files for this task, so "make the browser see one
// origin" is the only available option, not a workaround chosen over a
// simpler one. In production this is expected to be the same story (Nginx
// fronting both admin/'s static build and the API under one host, per root
// CLAUDE.md's deployment layout) — VITE_API_BASE_URL exists as an escape
// hatch if that ever isn't true, but is unset today.

const BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  '';

export class ApiError extends Error {
  constructor(code, message, status, body) {
    super(message || code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    // The full parsed response payload (or the raw text when it wasn't
    // JSON, or null when there was no body). Components render `code` only —
    // never `body` — but keeping it on the error lets the dev-only logging
    // below, and any screen that wants to, show the whole server response
    // without a second round-trip. See CLAUDE.md: a user should not have to
    // open devtools to know what failed, and a developer should not have to
    // guess what the server actually said.
    this.body = body ?? null;
  }
}

const IS_DEV =
  typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV === true;

// One place every failed request passes through — log the full server
// response to the console in development so it's visible without adding a
// try/catch at each call site. Silent in production.
function logApiFailure(method, path, err) {
  if (!IS_DEV) return;
  // eslint-disable-next-line no-console
  console.error(
    `[api] ${method} ${path} failed — ${err.code}${
      err.status ? ` (HTTP ${err.status})` : ''
    }: ${err.message}`,
    { code: err.code, status: err.status, response: err.body }
  );
}

function buildUrl(path, params) {
  const url = new URL(path, BASE_URL || window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }
  // Strip the origin back off when BASE_URL is relative/empty so the actual
  // fetch stays a same-origin relative request (important for the Vite dev
  // proxy, which matches on path prefix).
  return BASE_URL ? url.toString() : url.pathname + url.search;
}

// Resolves a backend path to a URL the BROWSER can open directly — a
// binary endpoint the fetch wrapper above can't hand back: a PDF download
// (<a href>), an image preview (<img src>). Not for JSON calls (use
// apiClient.get/post/...).
//
// Relative when BASE_URL is empty — dev (Vite's proxy forwards the path
// prefix, see admin/vite.config.js) and a same-origin production (Nginx
// routes the same prefix to the API). ABSOLUTE against the API origin when
// VITE_API_BASE_URL is set — a split-origin production, where a bare
// `/admin/...` href would otherwise resolve against the static frontend
// host and come back as index.html (Chrome then fails the PDF/image load
// with ERR_INVALID_RESPONSE). Params are appended the same way buildUrl
// does it, so `?v=...` cache-busting still works.
export function apiUrl(path, params) {
  const url = new URL(path, BASE_URL || window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }
  return BASE_URL ? url.toString() : url.pathname + url.search;
}

async function request(method, path, { params, body } = {}) {
  // A FormData body (the product-photo upload — see hooks/
  // useAdminCatalog.js's useUploadProductImage()) must reach fetch() as-is,
  // untouched by JSON.stringify, and WITHOUT an explicit Content-Type: the
  // browser sets `multipart/form-data; boundary=...` itself, which JS can't
  // replicate by hand. Every existing caller (post/put/patch below) keeps
  // passing a plain object and keeps getting JSON-encoded exactly as before
  // — this only branches when `body` is actually a FormData instance.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      credentials: 'include',
      headers:
        body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? (isFormData ? body : JSON.stringify(body)) : undefined,
    });
  } catch (networkErr) {
    // fetch() itself throws (offline, DNS failure, connection refused,
    // etc.) — not the same thing as a server-returned error response, and
    // there is no `{error:{code,message}}` body to parse here at all.
    const err = new ApiError('NETWORK_ERROR', networkErr.message, 0);
    logApiFailure(method, path, err);
    throw err;
  }

  let payload = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch (parseErr) {
      // A non-JSON body (an HTML error page from a proxy, a stray 502,
      // etc.) is not a shape this client can trust — treat it the same as
      // a payload-less response rather than throwing a raw parse error.
      payload = null;
    }
  }

  if (!response.ok) {
    const code =
      payload && payload.error && payload.error.code ? payload.error.code : 'INTERNAL_ERROR';
    const message =
      payload && payload.error && payload.error.message
        ? payload.error.message
        : response.statusText;
    // Hand the whole response to the error: the parsed `{error:{...}}` body
    // when we have one, otherwise the raw text (truncated) so a proxy's HTML
    // 502 page still shows up in the dev console.
    const responseBody = payload !== null ? payload : text ? text.slice(0, 2000) : null;
    const err = new ApiError(code, message, response.status, responseBody);
    logApiFailure(method, path, err);
    throw err;
  }

  return payload;
}

export const apiClient = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, body) => request('POST', path, { body: body ?? {} }),
  put: (path, body) => request('PUT', path, { body: body ?? {} }),
  // Added for the admin catalog screen's product-edit mutation (PATCH
  // /admin/catalog/products/:id — a real, partial-update route already on
  // the backend, see routes/adminCatalogRoutes.js) — every other admin/
  // mutation so far only ever needed POST/PUT.
  patch: (path, body) => request('PATCH', path, { body: body ?? {} }),
  // DELETE — no body. Used by the category editor and the settings
  // signature/stamp removal.
  delete: (path) => request('DELETE', path, {}),
  // Not a request — see apiUrl above. Builds the URL for a binary endpoint
  // opened directly by the browser (PDF <a href>, image <img src>).
  url: apiUrl,
};

export default apiClient;
